// src/client/services/kycService.ts
import { ok, err, type ServiceResult } from '@/lib/types';
import { isValidNRC } from '@client/utils/validation';
import { supabase } from '@client/lib/supabase';
import type { KycSubmissionFixture } from '@client/lib/fixtures';

export interface SubmitKycInput {
  frontDataUrl: string;
  backDataUrl: string;
  nrc: string;
}

interface SubmitKycResponse {
  kyc_id: string;
  status: 'pending' | 'verified' | 'manual_review' | 'rejected';
  nrc_front_path: string;
  nrc_back_path: string;
}

export async function submitKyc(input: SubmitKycInput): Promise<ServiceResult<KycSubmissionFixture>> {
  if (input.frontDataUrl.trim() === '' || input.backDataUrl.trim() === '') return err('photos_required');
  if (!isValidNRC(input.nrc)) return err('invalid_nrc');

  const { data, error } = await supabase.functions.invoke<SubmitKycResponse>('submit-kyc', {
    body: {
      nrc: input.nrc.trim(),
      front_data_url: input.frontDataUrl,
      back_data_url: input.backDataUrl,
    },
  });

  if (error) {
    const status = (error as { context?: { status?: number } }).context?.status ?? 0;
    if (status === 401) return err('unauthorized');
    if (status === 400) return err('invalid_input');
    if (status === 404) return err('profile_missing');
    if (status === 502) return err('upload_failed');
    return err('internal_error');
  }
  if (!data) return err('internal_error');

  const fixture: KycSubmissionFixture = {
    id: data.kyc_id,
    user_id: '', // not returned by the edge function; screen does not need it
    status: data.status,
    nrc_front_path: data.nrc_front_path,
    nrc_back_path: data.nrc_back_path,
    submitted_at: new Date().toISOString(),
    reviewed_at: null,
    rejection_reason: null,
  };
  return ok(fixture);
}

export async function getKycStatus(): Promise<ServiceResult<KycSubmissionFixture>> {
  // Latest submission for the caller. RLS scopes to caller's profile, so no
  // .eq('user_id', ...) is needed.
  const { data, error } = await supabase
    .from('kyc_submissions')
    .select('id, user_id, status, nrc_front_path, nrc_back_path, submitted_at, reviewed_at, review_notes')
    .order('submitted_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) return err('internal_error');
  if (!data) return err('not_submitted');
  const fixture: KycSubmissionFixture = {
    id: data.id as string,
    user_id: data.user_id as string,
    status: data.status as KycSubmissionFixture['status'],
    nrc_front_path: data.nrc_front_path as string,
    nrc_back_path: data.nrc_back_path as string,
    submitted_at: data.submitted_at as string,
    reviewed_at: (data.reviewed_at as string | null),
    rejection_reason: (data.review_notes as string | null),
  };
  return ok(fixture);
}
